import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    LayoutDashboard,
    FileText,
    Upload,
    Users,
    BarChart3,
    History,
    Activity,
    Mail,
    Landmark,
    CreditCard,
    Sparkles,
    Trash2,
    Wallet,
    MoreHorizontal
} from 'lucide-react';

const allNavItems = [
    {
        to: '/dashboard',
        icon: LayoutDashboard,
        label: 'Inicio',
        shortLabel: 'Inicio',
        roles: ['admin', 'supervisor', 'supervisor2'],
        priority: { admin: 1, supervisor: 1, supervisor2: 1 }
    },
    {
        to: '/dashboard/invoices',
        icon: FileText,
        label: 'Facturas',
        shortLabel: 'Facturas',
        roles: ['admin', 'supervisor', 'supervisor2', 'usuario'],
        priority: { admin: 2, supervisor: 2, supervisor2: 2, usuario: 2 }
    },
    {
        to: '/dashboard/upload',
        icon: Upload,
        label: 'Subir',
        shortLabel: 'Subir',
        roles: ['usuario'],
        priority: { usuario: 1 }
    },
    {
        to: '/dashboard/users',
        icon: Users,
        label: 'Usuarios',
        shortLabel: 'Usuarios',
        roles: ['admin', 'supervisor', 'supervisor2'],
        priority: { admin: 5, supervisor: 3, supervisor2: 3 }
    },
    {
        to: '/dashboard/totals',
        icon: BarChart3,
        label: 'Totales',
        shortLabel: 'Totales',
        roles: ['admin', 'supervisor'],
        priority: { admin: 6, supervisor: 4 }
    },
    {
        to: '/dashboard/daily-deposits',
        icon: Landmark,
        label: 'Depositos',
        shortLabel: 'Depósitos',
        roles: ['admin'],
        priority: { admin: 3 }
    },
    {
        to: '/dashboard/history',
        icon: History,
        label: 'Historial',
        shortLabel: 'Historial',
        roles: ['admin', 'supervisor'],
        priority: { admin: 7, supervisor: 5 }
    },
    {
        to: '/dashboard/activities',
        icon: Activity,
        label: 'Registro',
        shortLabel: 'Registro',
        roles: ['admin', 'supervisor'],
        priority: { admin: 8, supervisor: 6 }
    },
    {
        to: '/dashboard/email-extractions',
        icon: Mail,
        label: 'Correos',
        shortLabel: 'Correos',
        roles: ['admin', 'supervisor'],
        priority: { admin: 9, supervisor: 7 }
    },
    {
        to: '/dashboard/stripe-history',
        icon: CreditCard,
        label: 'Stripe',
        shortLabel: 'Stripe',
        roles: ['admin'],
        priority: { admin: 10 }
    },
    {
        to: '/dashboard/extra-accounting',
        icon: Sparkles,
        label: 'Contabilidad Extra',
        shortLabel: 'Extra',
        roles: ['admin'],
        priority: { admin: 11 }
    },
    {
        to: '/dashboard/trash',
        icon: Trash2,
        label: 'Papelera',
        shortLabel: 'Papelera',
        roles: ['admin'],
        priority: { admin: 12 }
    },
    {
        to: '/dashboard/paypal',
        icon: Wallet,
        label: 'Transacciones PayPal',
        shortLabel: 'PayPal',
        roles: ['admin'],
        flag: 'is_paypal',
        priority: { admin: 4, usuario: 3 }
    }
];

const MAX_BOTTOM_ITEMS = 6;

export const BottomNav = ({ onMoreClick }) => {
    const { user } = useAuth();
    const location = useLocation();

    if (!user) return null;

    const role = user.role;

    const filtered = allNavItems
        .filter(item => item.roles.includes(role) || (item.flag && user[item.flag]))
        .sort((a, b) => (a.priority[role] ?? 99) - (b.priority[role] ?? 99));

    const needsMore = filtered.length > MAX_BOTTOM_ITEMS;
    const visibleItems = needsMore ? filtered.slice(0, MAX_BOTTOM_ITEMS - 1) : filtered;

    return (
        <nav
            className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-slate-200 shadow-[0_-2px_10px_rgba(0,0,0,0.06)]"
            data-testid="bottom-nav"
        >
            <div className="flex items-stretch justify-around h-16 px-1">
                {visibleItems.map((item) => {
                    const Icon = item.icon;
                    const isActive = item.to === '/dashboard'
                        ? location.pathname === '/dashboard'
                        : location.pathname.startsWith(item.to);

                    return (
                        <NavLink
                            key={item.to}
                            to={item.to}
                            end={item.to === '/dashboard'}
                            className="flex flex-col items-center justify-center flex-1 min-w-0 py-1 transition-colors"
                            data-testid={`bottom-nav-${item.shortLabel.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s/g, '-')}`}
                        >
                            <div className={`flex items-center justify-center w-8 h-8 rounded-xl transition-all duration-200 ${
                                isActive
                                    ? 'bg-sky-500 text-white scale-110 shadow-md shadow-sky-200'
                                    : 'text-slate-400'
                            }`}>
                                <Icon className="w-5 h-5" />
                            </div>
                            <span className={`text-[10px] mt-0.5 font-medium leading-tight truncate max-w-full ${
                                isActive ? 'text-sky-600' : 'text-slate-400'
                            }`}>
                                {item.shortLabel}
                            </span>
                        </NavLink>
                    );
                })}

                {needsMore && (
                    <button
                        onClick={onMoreClick}
                        className="flex flex-col items-center justify-center flex-1 min-w-0 py-1 transition-colors"
                        data-testid="bottom-nav-more"
                        type="button"
                    >
                        <div className="flex items-center justify-center w-8 h-8 rounded-xl text-slate-400">
                            <MoreHorizontal className="w-5 h-5" />
                        </div>
                        <span className="text-[10px] mt-0.5 font-medium text-slate-400 leading-tight">
                            Mas
                        </span>
                    </button>
                )}
            </div>

            {/* Safe area for devices with home indicator */}
            <div className="h-[env(safe-area-inset-bottom,0px)] bg-white" />
        </nav>
    );
};
